import { APITypes } from '../primary-record-types';
import getName from './getName';
import urls from './urls';

export type OrgOrProfileCareTeamMember =
    | APITypes.medicalProfileService.CareTeamMemberDetails
    | APITypes.organizationService.OrganizationCareTeamMemberDetails;

export const isSupport = (
    careTeamMember: OrgOrProfileCareTeamMember,
): careTeamMember is APITypes.medicalProfileService.SupportCareTeamMemberDetails<APITypes.medicalProfileService.SupportCareTeamContactDetails> & {
    references: APITypes.sharedTypes.EntityReferences;
} => careTeamMember.careTeamMemberType === 'support';

export const isProvider = (
    careTeamMember: OrgOrProfileCareTeamMember,
): careTeamMember is APITypes.medicalProfileService.ProviderCareTeamMemberDetails<APITypes.medicalProfileService.ProviderCareTeamContactDetails> & {
    references: APITypes.sharedTypes.EntityReferences;
} => careTeamMember.careTeamMemberType === 'provider';

export const isInsurance = (
    careTeamMember: OrgOrProfileCareTeamMember,
): careTeamMember is APITypes.medicalProfileService.InsuranceCareTeamMemberDetails<APITypes.medicalProfileService.InsuranceCareTeamContactDetails> & {
    references: APITypes.sharedTypes.EntityReferences;
} => careTeamMember.careTeamMemberType === 'insurance';

type CareTeamMemberDescriptorName = {
    type: 'name';
    firstName: string;
    lastName: string;
};

type CareTeamMemberDescriptorText = {
    type: 'text';
    text: string;
};

type CareTeamMemberDescriptor = CareTeamMemberDescriptorName | CareTeamMemberDescriptorText;

export const getCareTeamMemberDescriptor = (careTeamMember: OrgOrProfileCareTeamMember): CareTeamMemberDescriptor => {
    if (isInsurance(careTeamMember))
        return {
            type: 'text',
            text: careTeamMember.metadata.insuranceProviderName,
        };
    const { firstName, lastName, company } = careTeamMember.contactDetails as any;
    if (company && !firstName && !lastName) return { type: 'text', text: company };
    return { firstName: firstName || '', lastName: lastName || '', type: 'name' };
};

export const getCareTeamMemberDescriptorDisplay = (careTeamMember: OrgOrProfileCareTeamMember) => {
    const descriptor = getCareTeamMemberDescriptor(careTeamMember);

    if (descriptor.type === 'text') return descriptor.text;
    return getName(descriptor);
};

export type ProfileSource = {
    source: 'profile';
    medicalProfileId: string;
};

export type OrganizationSource = {
    source: 'organization';
    organizationId: string;
};

export type CareTeamMemberSource = ProfileSource | OrganizationSource;

export const getCareTeamMemberAvatarUrl = (source: CareTeamMemberSource, careTeamMemberId: string | undefined) => {
    if (!careTeamMemberId) return null;

    return source.source === 'profile'
        ? urls.api.careTeamMembers.avatar(source.medicalProfileId, careTeamMemberId)
        : urls.api.organizations.careTeamMembers.careTeamMemberAvatar(source.organizationId, careTeamMemberId);
};

export const getCareTeamMemberInsuranceCardAvatarUrl = (
    source: CareTeamMemberSource,
    careTeamMemberId: string | undefined,
    frontOrBack: 'front' | 'back',
) => {
    if (!careTeamMemberId) return null;

    return source.source === 'profile'
        ? urls.api.careTeamMembers.insuranceCard(source.medicalProfileId, careTeamMemberId, frontOrBack)
        : urls.api.organizations.careTeamMembers.careTeamMemberInsuranceCard(
              source.organizationId,
              careTeamMemberId,
              frontOrBack,
          );
};
